import { useEffect, useState } from "react";

import { useFeatureflags } from "@web-app/api";
import { Icon } from "@web-app/components";
import { OrderCode, OrderScan } from "@web-app/types";
import {
  RenderedScannableCode,
  SCANNABLE_CODE_FEATURE,
  codeFormatLabel,
  renderScannableCodeSvg,
  resolveCodeFormat,
} from "@web-app/utils/scannableCode";

function ScannableCodeImage({
  code,
  format,
}: {
  code: string;
  format: string;
}) {
  const [rendered, setRendered] = useState<RenderedScannableCode | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    renderScannableCodeSvg(code, format)
      .catch(() => null)
      .then((result) => {
        if (isCancelled) return;
        setRendered(result);
        setIsLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [code, format]);

  if (isLoading) {
    return (
      <span
        className="h-[66px] w-[87px] animate-pulse rounded bg-gray-100"
        data-testid="barcode-loading"
      />
    );
  }

  if (!rendered) return null;

  return (
    // Shown at its rendered size so it stays scannable; it only shrinks when
    // the column is narrower than a long linear barcode.
    <img
      alt={`${codeFormatLabel(format)} barcode for ${code}`}
      className="h-auto max-w-full"
      height={rendered.height}
      width={rendered.width}
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(rendered.svg)}`}
      title={codeFormatLabel(format)}
    />
  );
}

const badge =
  "self-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase";

function ScannableCodeItem({
  format,
  item,
  scanCount,
}: {
  format: string;
  item: OrderCode;
  scanCount: number;
}) {
  return (
    // Sized to its contents so the details sit centred under the barcode.
    <div
      className="flex w-fit max-w-full flex-col items-center gap-1"
      data-testid="barcode-item"
    >
      <ScannableCodeImage code={item.code} format={format} />
      <span className="flex min-w-0 flex-wrap items-baseline justify-center gap-x-1.5 text-center">
        <span className="break-all">{item.code}</span>
        {item.quantity > 1 && (
          <span className="text-gray-400">×{item.quantity}</span>
        )}
        {item.isRequired && (
          <span className={`${badge} bg-gray-100 text-gray-500`}>Required</span>
        )}
        {scanCount > 0 && (
          <span
            aria-label={`Scanned ${scanCount} ${scanCount === 1 ? "time" : "times"}`}
            className={`${badge} inline-flex items-center gap-1 bg-green-50 text-green-700`}
            title={`Scanned ${scanCount} ${scanCount === 1 ? "time" : "times"}`}
          >
            <Icon name="clipboardCheck" className="h-2.5 w-2.5" />
            {scanCount}
          </span>
        )}
      </span>
    </div>
  );
}

// Required codes first, otherwise in the order they were entered.
const requiredFirst = (codes: OrderCode[] | null | undefined) =>
  [...(codes ?? [])].sort(
    (a, b) => Number(!!b.isRequired) - Number(!!a.isRequired),
  );

/**
 * "Barcode Details" row for a stop's section of the order summary: the codes
 * to scan there, each with how many times it has been scanned at the stop.
 * Renders nothing without codes, or when the scannableCode flag is off.
 */
export default function OrderScannableCodeDetails({
  codes,
  scans,
  workspaceDefaultFormat,
}: {
  codes?: OrderCode[] | null;
  scans?: OrderScan[] | null;
  workspaceDefaultFormat?: string | null;
}) {
  const { hasFeature } = useFeatureflags();
  const items = requiredFirst(codes);
  // Matched by code alone: the scanner may report another format.
  const scanCounts = new Map<string, number>();
  for (const scan of scans ?? []) {
    scanCounts.set(scan.code, (scanCounts.get(scan.code) ?? 0) + 1);
  }

  if (!hasFeature(SCANNABLE_CODE_FEATURE) || items.length === 0) return null;

  return (
    <div className="grid grid-cols-[1fr_2fr] gap-4">
      <span className="tw-details-field-title">Barcode Details</span>
      <div className="tw-details-field-value flex min-w-0 flex-col items-center gap-4">
        {items.map((item) => (
          <ScannableCodeItem
            key={item.code}
            item={item}
            scanCount={scanCounts.get(item.code) ?? 0}
            format={resolveCodeFormat(item.format, workspaceDefaultFormat)}
          />
        ))}
      </div>
    </div>
  );
}
